"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowLeftIcon, ArrowPathRoundedSquareIcon, BookmarkIcon, ChatBubbleOvalLeftIcon, CheckIcon, Cog6ToothIcon, HeartIcon, LinkIcon, PlayIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode, type SyntheticEvent } from "react";
import type HlsType from "hls.js";
import XIcon from "@/components/user/utils/icons/XIcon";
import type { TweetPreview, TweetPreviewMedia, TweetPreviewVideoVariant } from "@/lib/twitter";
import { parseTweetUrl } from "@/lib/twitter/url";
import { fetchTwitterPreview } from "@/services/api/twitter-preview-api";
import { useLinkPreviewContext } from "./LinkPreviewContext";
type PreviewState = {
  readonly type: "loading";
} | {
  readonly type: "ready";
  readonly preview: TweetPreview;
  readonly fallback: false;
} | {
  readonly type: "ready";
  readonly preview: TweetPreview;
  readonly fallback: true;
};
type VideoQualitySelection = {
  readonly type: "auto";
} | {
  readonly type: "variant";
  readonly url: string;
};
interface VideoQualityOption {
  readonly id: string;
  readonly label: string;
  readonly selection: VideoQualitySelection;
}
interface TwitterPreviewCardProps {
  readonly href: string;
  readonly tweetId: string;
}
const TWITTER_CARD_CLASSES = "tw-w-full tw-min-w-0 tw-max-w-full md:tw-max-w-[560px] tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-[#42566b] tw-bg-[#15202b] tw-text-[#f7f9f9] tw-shadow-sm";
const ACTION_CLASSES = "tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-full tw-border-0 tw-bg-transparent tw-px-2 tw-py-1 tw-text-sm tw-font-semibold tw-text-[#8b98a5] tw-no-underline tw-transition tw-duration-200 hover:tw-text-[#1d9bf0] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400";
const STATIC_ACTION_CLASSES = "tw-inline-flex tw-items-center tw-gap-x-2 tw-px-2 tw-py-1 tw-text-sm tw-font-semibold tw-text-[#8b98a5]";
function stopCardEvent(event: MouseEvent<HTMLElement>) {
  event.stopPropagation();
  event.nativeEvent.stopImmediatePropagation();
}
function stopMediaEvent(event: SyntheticEvent<HTMLElement>) {
  event.stopPropagation();
  event.nativeEvent.stopImmediatePropagation();
}
function readObjectRecord(value: unknown): Record<string, unknown> | undefined {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  return value as Record<string, unknown>;
}
function fallbackPreview(href: string, tweetId: string): TweetPreview {
  const parsed = parseTweetUrl(href);
  return {
    tweetId,
    url: href,
    ...(parsed?.authorHandle ? {
      authorHandle: parsed.authorHandle
    } : {})
  };
}
function getXPostPath(href: string): string {
  try {
    const url = new URL(href);
    return `${url.pathname}${url.search}`;
  } catch {
    return href;
  }
}
function formatTweetTimestamp(preview: TweetPreview): string | undefined {
  if (preview.createdAtIso) {
    const date = new Date(preview.createdAtIso);
    if (!Number.isNaN(date.getTime())) {
      const time = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit"
      }).format(date);
      const day = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      }).format(date);
      return `${time} · ${day}`;
    }
  }
  return preview.createdAtText;
}
function formatCount(value: number): string {
  if (value < 1000) {
    return value.toLocaleString();
  }
  const units = [{
    value: 1_000_000_000,
    suffix: "B"
  }, {
    value: 1_000_000,
    suffix: "M"
  }, {
    value: 1_000,
    suffix: "K"
  }];
  const unit = units.find(candidate => value >= candidate.value);
  if (!unit) {
    return value.toLocaleString();
  }
  const floored = Math.floor(value / unit.value * 10) / 10;
  return `${Number.isInteger(floored) ? floored.toFixed(0) : floored.toFixed(1)}${unit.suffix}`;
}
function formatViews(value: number): string {
  return `${formatCount(value)} ${value === 1 ? "View" : "Views"}`;
}
function formatBitrate(value: number): string {
  if (value >= 1_000_000) {
    const mbps = value / 1_000_000;
    return `${Number.isInteger(mbps) ? mbps.toFixed(0) : mbps.toFixed(1)} Mbps`;
  }
  return `${Math.round(value / 1000)} Kbps`;
}
function getVideoVariantLabel(variant: TweetPreviewVideoVariant, variants: readonly TweetPreviewVideoVariant[], index: number): string {
  if (variant.quality) {
    const qualityLabel = `${variant.quality}p`;
    const hasDuplicateQuality = variants.filter(candidate => candidate.quality === variant.quality).length > 1;
    if (hasDuplicateQuality && variant.bitrate) {
      return `${qualityLabel} ${formatBitrate(variant.bitrate)}`;
    }
    return qualityLabel;
  }
  if (variant.width && variant.height) {
    return `${variant.width}x${variant.height}`;
  }
  if (variant.bitrate) {
    return formatBitrate(variant.bitrate);
  }
  return `Option ${index + 1}`;
}
function buildVideoVariantOptions(videoUrl: string, variants: readonly TweetPreviewVideoVariant[] | undefined, hlsUrl: string | undefined): readonly TweetPreviewVideoVariant[] {
  if (!variants || variants.length === 0) {
    if (videoUrl === hlsUrl) {
      return [];
    }
    return [{
      url: videoUrl
    }];
  }
  if (variants.some(variant => variant.url === videoUrl)) {
    return variants;
  }
  return [{
    url: videoUrl
  }, ...variants];
}
function buildVideoQualityOptions({
  hlsUrl,
  variants,
  videoUrl
}: {
  readonly hlsUrl: string | undefined;
  readonly variants: readonly TweetPreviewVideoVariant[] | undefined;
  readonly videoUrl: string;
}): readonly VideoQualityOption[] {
  const manualOptions = buildVideoVariantOptions(videoUrl, variants, hlsUrl).map((variant, index, allVariants) => ({
    id: variant.url,
    label: getVideoVariantLabel(variant, allVariants, index),
    selection: {
      type: "variant" as const,
      url: variant.url
    }
  }));
  if (hlsUrl) {
    return [{
      id: "auto",
      label: "Auto",
      selection: {
        type: "auto"
      }
    }, ...manualOptions];
  }
  return manualOptions;
}
function getDefaultQualitySelection(hlsUrl: string | undefined, videoUrl: string): VideoQualitySelection {
  if (hlsUrl) {
    return {
      type: "auto"
    };
  }
  return {
    type: "variant",
    url: videoUrl
  };
}
function getQualitySelectionId(selection: VideoQualitySelection): string {
  if (selection.type === "auto") {
    return "auto";
  }
  return selection.url;
}
function isHlsSource(selection: VideoQualitySelection): boolean {
  return selection.type === "auto";
}
function getVideoSource(selection: VideoQualitySelection, hlsUrl: string | undefined, fallbackUrl: string): string {
  if (selection.type === "auto") {
    return hlsUrl ?? fallbackUrl;
  }
  return selection.url;
}
function updateAutoQualityFromHlsEvent(hls: HlsType, data: unknown, onAutoQualityChange: (quality: number | undefined) => void) {
  const levelIndex = readHlsEventLevelIndex(data);
  if (levelIndex === undefined) {
    onAutoQualityChange(undefined);
    return;
  }
  onAutoQualityChange(readHlsLevelQuality(hls.levels[levelIndex]));
}
function isFatalHlsError(data: unknown): boolean {
  const record = readObjectRecord(data);
  return record?.["fatal"] === true;
}
async function setupHlsSource(videoElement: HTMLVideoElement, src: string, hlsRef: {
  current: HlsType | null;
}, onAutoQualityChange: (quality: number | undefined) => void, onFatalError: () => void, isCancelled: () => boolean): Promise<boolean> {
  if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
    if (isCancelled()) {
      return false;
    }
    videoElement.src = src;
    videoElement.load();
    onAutoQualityChange(undefined);
    return true;
  }
  const mod = await import("hls.js");
  if (isCancelled()) {
    return false;
  }
  const HlsConstructor = mod.default;
  if (HlsConstructor.isSupported()) {
    const hls = new HlsConstructor({
      startLevel: -1,
      enableWorker: true
    });
    if (isCancelled()) {
      hls.destroy();
      return false;
    }
    hlsRef.current = hls;
    const handleQualityEvent = (_event: unknown, data: unknown) => {
      updateAutoQualityFromHlsEvent(hls, data, onAutoQualityChange);
    };
    const cleanupHls = () => {
      hls.off(HlsConstructor.Events.LEVEL_SWITCHED, handleQualityEvent);
      hls.off(HlsConstructor.Events.FRAG_CHANGED, handleQualityEvent);
      hls.off(HlsConstructor.Events.ERROR, handleErrorEvent);
      hls.destroy();
      if (hlsRef.current === hls) {
        hlsRef.current = null;
      }
    };
    const handleErrorEvent = (_event: unknown, data: unknown) => {
      if (!isFatalHlsError(data) || isCancelled()) {
        return;
      }
      cleanupHls();
      if (isCancelled()) {
        return;
      }
      onAutoQualityChange(undefined);
      onFatalError();
    };
    hls.on(HlsConstructor.Events.LEVEL_SWITCHED, handleQualityEvent);
    hls.on(HlsConstructor.Events.FRAG_CHANGED, handleQualityEvent);
    hls.on(HlsConstructor.Events.ERROR, handleErrorEvent);
    hls.loadSource(src);
    hls.attachMedia(videoElement);
    return true;
  }
  return false;
}
function readHlsEventLevelIndex(value: unknown): number | undefined {
  const record = readObjectRecord(value);
  if (record) {
    const level = record["level"];
    if (typeof level === "number" && Number.isFinite(level)) {
      return level;
    }
    const frag = readObjectRecord(record["frag"]);
    if (frag) {
      const fragLevel = frag["level"];
      if (typeof fragLevel === "number" && Number.isFinite(fragLevel)) {
        return fragLevel;
      }
    }
  }
  return undefined;
}
function readHlsLevelQuality(level: unknown): number | undefined {
  const record = readObjectRecord(level);
  if (record) {
    const width = typeof record["width"] === "number" ? record["width"] : undefined;
    const height = typeof record["height"] === "number" ? record["height"] : undefined;
    return width && height ? Math.min(width, height) : undefined;
  }
  return undefined;
}
function snapshotVideoPlayback(videoElement: HTMLVideoElement): {
  readonly currentTime: number;
  readonly wasPlaying: boolean;
} {
  return {
    currentTime: videoElement.currentTime,
    wasPlaying: !videoElement.paused && !videoElement.ended
  };
}
function restoreVideoPlayback(videoElement: HTMLVideoElement, snapshot: {
  readonly currentTime: number;
  readonly wasPlaying: boolean;
}, isCancelled: () => boolean): void {
  const restore = () => {
    if (isCancelled()) {
      return;
    }
    if (Number.isFinite(snapshot.currentTime) && snapshot.currentTime > 0) {
      videoElement.currentTime = Math.min(snapshot.currentTime, videoElement.duration || snapshot.currentTime);
    }
    if (snapshot.wasPlaying) {
      void videoElement.play();
    }
  };
  if (videoElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
    restore();
    return;
  }
  videoElement.addEventListener("loadedmetadata", restore, {
    once: true
  });
}
function TwitterHandleLink(t0) {
  const $ = _c(3);
  const {
    handle
  } = t0;
  const t1 = `https://x.com/${handle}`;
  let t2;
  if ($[0] !== handle || $[1] !== t1) {
    t2 = <Link href={t1} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-text-[#6ecbff] tw-no-underline hover:tw-underline">@{handle}</Link>;
    $[0] = handle;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function renderTweetText(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const mentionPattern = /(^|[^\w])@(\w{1,15})(?=\b)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = mentionPattern.exec(text)) !== null) {
    const prefix = match[1] ?? "";
    const handle = match[2];
    if (!handle) {
      continue;
    }
    const mentionStart = match.index + prefix.length;
    const mention = `@${handle}`;
    if (mentionStart > lastIndex) {
      parts.push(text.slice(lastIndex, mentionStart));
    }
    parts.push(<TwitterHandleLink key={`${handle}-${mentionStart}`} handle={handle} />);
    lastIndex = mentionStart + mention.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}
function LoadingCard() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-h-10 tw-w-10 tw-rounded-full tw-bg-[#263544]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="tw-flex tw-items-center tw-gap-x-3">{t0}<div className="tw-flex tw-flex-col tw-gap-y-2"><div className="tw-h-4 tw-w-40 tw-rounded tw-bg-[#263544]" /><div className="tw-h-3 tw-w-36 tw-rounded tw-bg-[#263544]/80" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-space-y-2"><div className="tw-h-4 tw-w-full tw-rounded tw-bg-[#263544]" /><div className="tw-h-4 tw-w-11/12 tw-rounded tw-bg-[#263544]" /></div>;
    t3 = <div className="tw-aspect-[4/3] tw-min-h-72 tw-rounded-xl tw-bg-[#263544]/80" />;
    t4 = <div className="tw-h-4 tw-w-44 tw-rounded tw-bg-[#263544]" />;
    t5 = <div className="tw-h-px tw-w-full tw-bg-[#42566b]" />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className={`${TWITTER_CARD_CLASSES} tw-p-4`} data-testid="twitter-post-skeleton"><div className="tw-flex tw-animate-pulse tw-flex-col tw-gap-y-4">{t1}{t2}{t3}{t4}{t5}<div className="tw-flex tw-gap-x-8"><div className="tw-h-4 tw-w-16 tw-rounded tw-bg-[#263544]" /><div className="tw-h-4 tw-w-20 tw-rounded tw-bg-[#263544]" /><div className="tw-h-4 tw-w-24 tw-rounded tw-bg-[#263544]" /></div></div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}
function UnavailableCard(t0) {
  const $ = _c(18);
  const {
    href,
    copied,
    onCopy
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#0f1720] tw-p-2"><XIcon /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="tw-m-0 tw-text-lg tw-font-semibold tw-leading-6 tw-text-[#f7f9f9]">Tweet preview unavailable</p>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== href) {
    t3 = <div className="tw-flex tw-items-start tw-gap-x-3">{t1}<div className="tw-min-w-0 tw-flex-1">{t2}<Link href={href} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-mt-1 tw-block tw-truncate tw-text-sm tw-leading-5 tw-text-[#8b98a5] tw-no-underline hover:tw-text-[#f7f9f9]">{href}</Link></div></div>;
    $[2] = href;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== href) {
    t4 = <Link href={href} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-[#42566b] tw-bg-transparent tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-[#6ecbff] tw-no-underline tw-transition hover:tw-border-[#5a7088] hover:tw-text-[#9bddff]">Open on X</Link>;
    $[4] = href;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <LinkIcon className="tw-h-5 tw-w-5" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = copied ? "Copied" : "Copy";
  let t7;
  if ($[7] !== t6) {
    t7 = <span>{t6}</span>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== onCopy || $[10] !== t7) {
    t8 = <button type="button" onClick={onCopy} className={`${ACTION_CLASSES} tw-w-full tw-justify-center`}>{t5}{t7}</button>;
    $[9] = onCopy;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t4 || $[13] !== t8) {
    t9 = <div className="tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-2">{t4}{t8}</div>;
    $[12] = t4;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t3 || $[16] !== t9) {
    t10 = <article className={TWITTER_CARD_CLASSES} data-testid="twitter-post-fallback"><div className="tw-flex tw-flex-col tw-gap-y-3 tw-p-4">{t3}{t9}</div></article>;
    $[15] = t3;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}
function AuthorAvatar(t0) {
  const $ = _c(6);
  const {
    authorHref,
    authorName,
    profileImageUrl
  } = t0;
  let t1;
  if ($[0] !== authorName || $[1] !== profileImageUrl) {
    t1 = profileImageUrl ? <img src={profileImageUrl} alt={authorName} className="tw-h-10 tw-w-10 tw-flex-shrink-0 tw-rounded-full tw-object-cover" loading="lazy" /> : <div className="tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#0f1720] tw-p-2"><XIcon /></div>;
    $[0] = authorName;
    $[1] = profileImageUrl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== authorHref || $[4] !== t1) {
    t2 = <Link href={authorHref} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-flex-shrink-0 tw-text-inherit tw-no-underline">{t1}</Link>;
    $[3] = authorHref;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function TweetAuthorBlock(t0) {
  const $ = _c(10);
  const {
    authorHref,
    authorName,
    authorHandle,
    followHref
  } = t0;
  let t1;
  if ($[0] !== authorHref || $[1] !== authorName) {
    t1 = <Link href={authorHref} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-line-clamp-1 tw-text-sm tw-font-bold tw-leading-4 tw-text-[#f7f9f9] tw-no-underline hover:tw-underline">{authorName}</Link>;
    $[0] = authorHref;
    $[1] = authorName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== authorHandle || $[4] !== authorHref || $[5] !== followHref) {
    t2 = authorHandle && <div className="tw-line-clamp-1 tw-text-xs tw-leading-4 tw-text-[#8b98a5]"><Link href={authorHref} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-text-[#8b98a5] tw-no-underline hover:tw-underline">@{authorHandle}</Link><span className="tw-px-1">·</span><Link href={followHref ?? authorHref} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-font-semibold tw-text-[#6ecbff] tw-no-underline hover:tw-text-[#9bddff]">Follow</Link></div>;
    $[3] = authorHandle;
    $[4] = authorHref;
    $[5] = followHref;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className="tw-min-w-0">{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function TweetHeader(t0) {
  const $ = _c(18);
  const {
    authorHref,
    authorName,
    followHref,
    href,
    preview
  } = t0;
  let t1;
  if ($[0] !== authorHref || $[1] !== authorName || $[2] !== preview.authorProfileImageUrl) {
    t1 = <AuthorAvatar authorHref={authorHref} authorName={authorName} profileImageUrl={preview.authorProfileImageUrl} />;
    $[0] = authorHref;
    $[1] = authorName;
    $[2] = preview.authorProfileImageUrl;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== authorHref || $[5] !== authorName || $[6] !== followHref || $[7] !== preview.authorHandle) {
    t2 = <TweetAuthorBlock authorHref={authorHref} authorName={authorName} authorHandle={preview.authorHandle} followHref={followHref} />;
    $[4] = authorHref;
    $[5] = authorName;
    $[6] = followHref;
    $[7] = preview.authorHandle;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-x-3">{t1}{t2}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <XIcon />;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== href) {
    t5 = <Link href={href} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} aria-label="Open post on X" className="tw-h-7 tw-w-7 tw-flex-shrink-0 tw-text-[#f7f9f9] tw-transition hover:tw-text-white">{t4}</Link>;
    $[13] = href;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t5) {
    t6 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-3">{t3}{t5}</div>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function TweetBodyText(t0) {
  const $ = _c(12);
  const {
    authorName,
    href,
    replyToHandle,
    text
  } = t0;
  if (text) {
    let t1;
    if ($[0] !== replyToHandle) {
      t1 = replyToHandle && <p className="tw-m-0 tw-text-sm tw-font-semibold tw-leading-5 tw-text-[#8b98a5]">Replying to <TwitterHandleLink handle={replyToHandle} /></p>;
      $[0] = replyToHandle;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== text) {
      t2 = renderTweetText(text);
      $[2] = text;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t2) {
      t3 = <p className="tw-m-0 tw-whitespace-pre-line tw-break-words tw-text-base tw-font-normal tw-leading-6 tw-text-[#f7f9f9]">{t2}</p>;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t1 || $[7] !== t3) {
      t4 = <div className="tw-flex tw-flex-col tw-gap-y-1">{t1}{t3}</div>;
      $[6] = t1;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t1;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="tw-m-0 tw-text-lg tw-font-semibold tw-leading-6 tw-text-[#f7f9f9]">Tweet preview unavailable</p>;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const t2 = href || authorName;
  let t3;
  if ($[10] !== t2) {
    t3 = <div className="tw-flex tw-flex-col tw-gap-y-2">{t1}<p className="tw-m-0 tw-break-all tw-text-sm tw-leading-5 tw-text-[#8b98a5]">{t2}</p></div>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function VideoQualityMenu(t0) {
  const $ = _c(27);
  const {
    autoQuality,
    isOpen,
    onClose,
    onSelect,
    options,
    selection
  } = t0;
  if (!isOpen) {
    return null;
  }
  let t1;
  if ($[0] !== selection) {
    t1 = getQualitySelectionId(selection);
    $[0] = selection;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedId = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span aria-hidden="true" className="tw-absolute tw-right-8 tw-top-[-7px] tw-h-0 tw-w-0 tw-border-x-8 tw-border-b-8 tw-border-x-transparent tw-border-b-[#171717]" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClose) {
    t3 = event => {
      stopMediaEvent(event);
      onClose();
    };
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowLeftIcon className="tw-h-4 tw-w-4" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <button type="button" aria-label="Close video quality" onClick={t3} className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0 tw-text-white hover:tw-bg-white/10 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400">{t4}</button>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="tw-text-xs tw-font-semibold tw-leading-6 tw-text-white">Video quality</span>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <div className="tw-flex tw-items-center tw-gap-x-2 tw-py-2">{t5}{t6}</div>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== autoQuality || $[12] !== onClose || $[13] !== onSelect || $[14] !== options || $[15] !== selectedId) {
    let t9;
    if ($[17] !== autoQuality || $[18] !== onClose || $[19] !== onSelect || $[20] !== selectedId) {
      t9 = option => {
        const isSelected = option.id === selectedId;
        return <button key={option.id} type="button" role="radio" aria-checked={isSelected} onClick={event_0 => {
          stopMediaEvent(event_0);
          onSelect(option.selection);
          onClose();
        }} className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-x-5 tw-border-0 tw-bg-transparent tw-px-1 tw-py-1.5 tw-text-left tw-text-[13px] tw-font-semibold tw-text-white hover:tw-bg-white/10"><span>{option.label}{option.id === "auto" && autoQuality && <span className="tw-ml-1 tw-text-[#8b98a5]">({autoQuality}p)</span>}</span><span aria-hidden="true" className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-solid ${isSelected ? "tw-border-[#1d9bf0] tw-bg-[#1d9bf0]" : "tw-border-[#8b98a5]"}`}>{isSelected && <CheckIcon className="tw-h-3.5 tw-w-3.5" />}</span></button>;
      };
      $[17] = autoQuality;
      $[18] = onClose;
      $[19] = onSelect;
      $[20] = selectedId;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    t8 = options.map(t9);
    $[11] = autoQuality;
    $[12] = onClose;
    $[13] = onSelect;
    $[14] = options;
    $[15] = selectedId;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <div className="tw-rounded-lg tw-bg-black tw-px-2.5 tw-py-2" role="radiogroup" aria-label="Video quality">{t8}</div>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t7 || $[25] !== t9) {
    t10 = <dialog open={true} className="tw-absolute tw-left-auto tw-right-3 tw-top-11 tw-z-30 tw-m-0 tw-w-fit tw-min-w-40 tw-max-w-[calc(100%-1.5rem)] tw-overflow-hidden tw-rounded-xl tw-border-0 tw-bg-[#171717] tw-px-1.5 tw-pb-1.5 tw-pt-0 tw-text-white tw-shadow-xl" aria-label="Video quality">{t2}{t7}{t9}</dialog>;
    $[24] = t7;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
function TwitterVideoPlayer(t0) {
  const $ = _c(38);
  const {
    allowQualitySelection: t1,
    autoPlay,
    captionsUrl,
    className,
    hlsUrl,
    posterUrl,
    variants,
    videoUrl
  } = t0;
  const allowQualitySelection = t1 === undefined ? true : t1;
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const fallbackPlaybackSnapshotRef = useRef(null);
  const qualitySwitchSnapshotRef = useRef(null);
  let t2;
  if ($[0] !== hlsUrl || $[1] !== videoUrl) {
    t2 = {
      hlsUrl,
      videoUrl
    };
    $[0] = hlsUrl;
    $[1] = videoUrl;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const previousSourceRef = useRef(t2);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentAutoQuality, setCurrentAutoQuality] = useState(undefined);
  let t3;
  if ($[3] !== hlsUrl || $[4] !== videoUrl) {
    t3 = () => getDefaultQualitySelection(hlsUrl, videoUrl);
    $[3] = hlsUrl;
    $[4] = videoUrl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [selection, setSelection] = useState(t3);
  let t4;
  if ($[6] !== hlsUrl || $[7] !== variants || $[8] !== videoUrl) {
    t4 = buildVideoQualityOptions({
      hlsUrl,
      variants,
      videoUrl
    });
    $[6] = hlsUrl;
    $[7] = variants;
    $[8] = videoUrl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const qualityOptions = t4;
  let t5;
  let t6;
  if ($[10] !== hlsUrl || $[11] !== videoUrl) {
    t5 = () => {
      const previousSource = previousSourceRef.current;
      if (previousSource.hlsUrl === hlsUrl && previousSource.videoUrl === videoUrl) {
        return;
      }
      previousSourceRef.current = {
        hlsUrl,
        videoUrl
      };
      setSelection(getDefaultQualitySelection(hlsUrl, videoUrl));
      setIsMenuOpen(false);
    };
    t6 = [hlsUrl, videoUrl];
    $[10] = hlsUrl;
    $[11] = videoUrl;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[14] !== hlsUrl || $[15] !== selection || $[16] !== videoUrl) {
    t7 = () => {
      const videoElement = videoRef.current;
      if (!videoElement) {
        return;
      }
      let cancelled = false;
      const playbackSnapshot = qualitySwitchSnapshotRef.current ?? fallbackPlaybackSnapshotRef.current ?? snapshotVideoPlayback(videoElement);
      qualitySwitchSnapshotRef.current = null;
      fallbackPlaybackSnapshotRef.current = null;
      const destroyHls = () => {
        hlsRef.current?.destroy();
        hlsRef.current = null;
      };
      const loadFallbackSource = () => {
        fallbackPlaybackSnapshotRef.current = playbackSnapshot;
        setSelection({
          type: "variant",
          url: videoUrl
        });
        setCurrentAutoQuality(undefined);
      };
      destroyHls();
      const src = getVideoSource(selection, hlsUrl, videoUrl);
      if (isHlsSource(selection) && hlsUrl) {
        setupHlsSource(videoElement, hlsUrl, hlsRef, setCurrentAutoQuality, loadFallbackSource, () => cancelled).then(loaded => {
          if (cancelled) {
            destroyHls();
            return;
          }
          if (!loaded) {
            loadFallbackSource();
            return;
          }
          restoreVideoPlayback(videoElement, playbackSnapshot, () => cancelled);
        }).catch(() => {
          if (!cancelled) {
            loadFallbackSource();
          }
        });
      } else {
        setCurrentAutoQuality(undefined);
        videoElement.src = src;
        videoElement.load();
        restoreVideoPlayback(videoElement, playbackSnapshot, () => cancelled);
      }
      return () => {
        cancelled = true;
        destroyHls();
      };
    };
    t8 = [hlsUrl, selection, videoUrl];
    $[14] = hlsUrl;
    $[15] = selection;
    $[16] = videoUrl;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  let t9;
  if ($[19] !== allowQualitySelection || $[20] !== currentAutoQuality || $[21] !== isMenuOpen || $[22] !== qualityOptions || $[23] !== selection) {
    t9 = allowQualitySelection && qualityOptions.length > 1 && <>{isMenuOpen && <button type="button" aria-label="Close video quality menu" onClick={event => {
        stopMediaEvent(event);
        setIsMenuOpen(false);
      }} className="tw-absolute tw-inset-0 tw-z-10 tw-border-0 tw-bg-transparent tw-p-0" />}<button type="button" aria-label="Video quality" aria-expanded={isMenuOpen} onClick={event_0 => {
        stopMediaEvent(event_0);
        setIsMenuOpen(_temp);
      }} className="tw-absolute tw-right-2.5 tw-top-2.5 tw-z-20 tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-black/45 tw-p-0 tw-text-white/65 tw-backdrop-blur-sm tw-transition hover:tw-bg-black/75 hover:tw-text-white focus:tw-outline-none focus-visible:tw-bg-black/75 focus-visible:tw-text-white focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400"><Cog6ToothIcon className="tw-h-4 tw-w-4" /></button><VideoQualityMenu autoQuality={currentAutoQuality} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} onSelect={opt => {
        if (videoRef.current) {
          qualitySwitchSnapshotRef.current = snapshotVideoPlayback(videoRef.current);
        }
        setSelection(opt);
      }} options={qualityOptions} selection={selection} /></>;
    $[19] = allowQualitySelection;
    $[20] = currentAutoQuality;
    $[21] = isMenuOpen;
    $[22] = qualityOptions;
    $[23] = selection;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== isMenuOpen) {
    t10 = event_1 => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
      stopMediaEvent(event_1);
    };
    $[25] = isMenuOpen;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = captionsUrl ?? "data:text/vtt,WEBVTT";
  let t12;
  if ($[27] !== t11) {
    t12 = <track kind="captions" src={t11} srcLang="en" label="Captions" default={true} />;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== autoPlay || $[30] !== className || $[31] !== posterUrl || $[32] !== t10 || $[33] !== t12) {
    t13 = <video ref={videoRef} poster={posterUrl} className={className} controls={true} playsInline={true} preload="metadata" autoPlay={autoPlay} onClick={t10}>{t12}</video>;
    $[29] = autoPlay;
    $[30] = className;
    $[31] = posterUrl;
    $[32] = t10;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13 || $[36] !== t9) {
    t14 = <div className="tw-relative tw-h-full tw-w-full">{t9}{t13}</div>;
    $[35] = t13;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
function _temp(value) {
  return !value;
}
function TweetVideo(t0) {
  const $ = _c(6);
  const {
    captionsUrl,
    hlsUrl,
    posterUrl,
    variants,
    videoUrl
  } = t0;
  let t1;
  if ($[0] !== captionsUrl || $[1] !== hlsUrl || $[2] !== posterUrl || $[3] !== variants || $[4] !== videoUrl) {
    t1 = <div className="tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-[#42566b] tw-bg-black"><TwitterVideoPlayer captionsUrl={captionsUrl} className="tw-block tw-h-auto tw-max-h-[24rem] tw-w-full tw-object-contain" hlsUrl={hlsUrl} posterUrl={posterUrl} variants={variants} videoUrl={videoUrl} /></div>;
    $[0] = captionsUrl;
    $[1] = hlsUrl;
    $[2] = posterUrl;
    $[3] = variants;
    $[4] = videoUrl;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function TweetMediaGridVideo(t0) {
  const $ = _c(13);
  const {
    media
  } = t0;
  const [playing, setPlaying] = useState(false);
  const posterUrl = media.posterUrl ?? media.imageUrl;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = event => {
      stopCardEvent(event);
      setPlaying(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const playVideo = t1;
  if (posterUrl && !playing) {
    let t2;
    if ($[1] !== posterUrl) {
      t2 = <img src={posterUrl} alt="Tweet video" className="tw-h-full tw-w-full tw-object-cover" loading="lazy" />;
      $[1] = posterUrl;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <span className="tw-absolute tw-bottom-3 tw-left-3 tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-black/70 tw-text-white"><PlayIcon className="tw-h-4 tw-w-4 tw-fill-current" /></span>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t2) {
      t4 = <button type="button" onClick={playVideo} className="tw-relative tw-block tw-h-full tw-w-full tw-border-0 tw-bg-black tw-p-0">{t2}{t3}</button>;
      $[4] = t2;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  let t2;
  if ($[6] !== media.captionsUrl || $[7] !== media.videoHlsUrl || $[8] !== media.videoUrl || $[9] !== media.videoVariants || $[10] !== playing || $[11] !== posterUrl) {
    t2 = media.videoUrl ? <TwitterVideoPlayer allowQualitySelection={false} autoPlay={playing} captionsUrl={media.captionsUrl} className="tw-h-full tw-w-full tw-object-contain" hlsUrl={media.videoHlsUrl} posterUrl={posterUrl} variants={media.videoVariants} videoUrl={media.videoUrl} /> : null;
    $[6] = media.captionsUrl;
    $[7] = media.videoHlsUrl;
    $[8] = media.videoUrl;
    $[9] = media.videoVariants;
    $[10] = playing;
    $[11] = posterUrl;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}
function TweetMediaGridItem(t0) {
  const $ = _c(8);
  const {
    alt,
    href,
    media
  } = t0;
  if (media.type === "video" && media.videoUrl) {
    let t1;
    if ($[0] !== media) {
      t1 = <TweetMediaGridVideo media={media} />;
      $[0] = media;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (media.imageUrl) {
    let t1;
    if ($[2] !== alt || $[3] !== media.imageUrl) {
      t1 = <img src={media.imageUrl} alt={alt} className="tw-h-full tw-w-full tw-object-cover" loading="lazy" />;
      $[2] = alt;
      $[3] = media.imageUrl;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== href || $[6] !== t1) {
      t2 = <Link href={href} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-block tw-h-full tw-w-full tw-no-underline">{t1}</Link>;
      $[5] = href;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  return null;
}
function TweetMediaGrid(t0) {
  const $ = _c(11);
  const {
    alt,
    href,
    mediaItems
  } = t0;
  let t1;
  let t2;
  if ($[0] !== alt || $[1] !== href || $[2] !== mediaItems) {
    const visibleMediaItems = mediaItems.slice(0, 4);
    t1 = "tw-grid tw-aspect-video tw-grid-cols-2 tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-[#42566b] tw-bg-black";
    let t3;
    if ($[5] !== alt || $[6] !== href) {
      t3 = (media, index) => <div key={media.videoUrl ?? media.imageUrl ?? media.posterUrl ?? index} className="tw-min-h-0 tw-min-w-0 tw-overflow-hidden tw-border-0 tw-border-solid tw-border-[#42566b] odd:tw-border-r [&:nth-child(-n+2)]:tw-border-b"><TweetMediaGridItem alt={alt} href={href} media={media} /></div>;
      $[5] = alt;
      $[6] = href;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = visibleMediaItems.map(t3);
    $[0] = alt;
    $[1] = href;
    $[2] = mediaItems;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function TweetImage(t0) {
  const $ = _c(6);
  const {
    alt,
    href,
    imageUrl
  } = t0;
  let t1;
  if ($[0] !== alt || $[1] !== imageUrl) {
    t1 = <img src={imageUrl} alt={alt} className="tw-h-auto tw-max-h-[24rem] tw-w-full tw-object-contain" loading="lazy" />;
    $[0] = alt;
    $[1] = imageUrl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== href || $[4] !== t1) {
    t2 = <Link href={href} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-block tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-[#42566b] tw-bg-black tw-no-underline">{t1}</Link>;
    $[3] = href;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function TweetMediaLink(t0) {
  const $ = _c(2);
  const {
    mediaLink
  } = t0;
  let t1;
  if ($[0] !== mediaLink) {
    t1 = <Link href={mediaLink} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-flex tw-min-h-24 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-[#42566b] tw-bg-[#0f1720] tw-p-4 tw-text-center tw-text-sm tw-font-semibold tw-text-[#8b98a5] tw-no-underline tw-transition hover:tw-border-[#5a7088] hover:tw-text-[#f7f9f9]">Media on X</Link>;
    $[0] = mediaLink;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function TweetMedia(t0) {
  const $ = _c(16);
  const {
    authorName,
    href,
    preview
  } = t0;
  if (preview.media && preview.media.length > 1) {
    const t1 = preview.text ?? authorName;
    let t2;
    if ($[0] !== href || $[1] !== preview.media || $[2] !== t1) {
      t2 = <TweetMediaGrid alt={t1} href={href} mediaItems={preview.media} />;
      $[0] = href;
      $[1] = preview.media;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (preview.mediaVideoUrl) {
    const t1 = preview.mediaPosterUrl ?? preview.mediaImageUrl;
    let t2;
    if ($[4] !== preview.mediaCaptionsUrl || $[5] !== preview.mediaVideoHlsUrl || $[6] !== preview.mediaVideoUrl || $[7] !== preview.mediaVideoVariants || $[8] !== t1) {
      t2 = <TweetVideo captionsUrl={preview.mediaCaptionsUrl} hlsUrl={preview.mediaVideoHlsUrl} posterUrl={t1} variants={preview.mediaVideoVariants} videoUrl={preview.mediaVideoUrl} />;
      $[4] = preview.mediaCaptionsUrl;
      $[5] = preview.mediaVideoHlsUrl;
      $[6] = preview.mediaVideoUrl;
      $[7] = preview.mediaVideoVariants;
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  if (preview.mediaImageUrl) {
    const t1 = preview.text ?? authorName;
    let t2;
    if ($[10] !== href || $[11] !== preview.mediaImageUrl || $[12] !== t1) {
      t2 = <TweetImage alt={t1} href={href} imageUrl={preview.mediaImageUrl} />;
      $[10] = href;
      $[11] = preview.mediaImageUrl;
      $[12] = t1;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    return t2;
  }
  if (preview.mediaLink) {
    let t1;
    if ($[14] !== preview.mediaLink) {
      t1 = <TweetMediaLink mediaLink={preview.mediaLink} />;
      $[14] = preview.mediaLink;
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    return t1;
  }
  return null;
}
function TweetActions(t0) {
  const $ = _c(29);
  const {
    preview,
    tweetId
  } = t0;
  const t1 = `https://x.com/intent/tweet?in_reply_to=${tweetId}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChatBubbleOvalLeftIcon className="tw-h-5 tw-w-5" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== preview.conversationCount) {
    t3 = preview.conversationCount === undefined ? "0" : formatCount(preview.conversationCount);
    $[1] = preview.conversationCount;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = <span>{t3}</span>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t4) {
    t5 = <Link href={t1} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} aria-label="Reply" className={ACTION_CLASSES}>{t2}{t4}</Link>;
    $[5] = t1;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = `https://x.com/intent/retweet?tweet_id=${tweetId}`;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ArrowPathRoundedSquareIcon className="tw-h-5 tw-w-5" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== preview.retweetCount) {
    t8 = preview.retweetCount !== undefined && preview.retweetCount > 0 && <span>{formatCount(preview.retweetCount)}</span>;
    $[9] = preview.retweetCount;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <Link href={t6} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} aria-label="Repost" className={`${ACTION_CLASSES} hover:tw-text-[#00ba7c]`}>{t7}{t8}</Link>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = `https://x.com/intent/like?tweet_id=${tweetId}`;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <HeartIcon className="tw-h-5 tw-w-5" />;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== preview.favoriteCount) {
    t12 = preview.favoriteCount === undefined ? "0" : formatCount(preview.favoriteCount);
    $[15] = preview.favoriteCount;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12) {
    t13 = <span>{t12}</span>;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t10 || $[20] !== t13) {
    t14 = <Link href={t10} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} aria-label="Like" className={`${ACTION_CLASSES} hover:tw-text-[#f91880]`}>{t11}{t13}</Link>;
    $[19] = t10;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== preview.bookmarkCount) {
    t15 = preview.bookmarkCount !== undefined && <div className={STATIC_ACTION_CLASSES} aria-label="Bookmarks"><BookmarkIcon className="tw-h-5 tw-w-5" /><span>{formatCount(preview.bookmarkCount)}</span></div>;
    $[22] = preview.bookmarkCount;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t14 || $[25] !== t15 || $[26] !== t5 || $[27] !== t9) {
    t16 = <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-2 tw-px-10">{t5}{t9}{t14}{t15}</div>;
    $[24] = t14;
    $[25] = t15;
    $[26] = t5;
    $[27] = t9;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  return t16;
}
export default function TwitterPreviewCard(t0) {
  const $ = _c(59);
  const {
    href,
    tweetId
  } = t0;
  const {
    hideActions
  } = useLinkPreviewContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: "loading"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, setState] = useState(t1);
  const [copied, setCopied] = useState(false);
  let t2;
  let t3;
  if ($[1] !== href || $[2] !== tweetId) {
    t2 = () => {
      let active = true;
      setState({
        type: "loading"
      });
      fetchTwitterPreview(href).then(preview => {
        if (active) {
          setState({
            type: "ready",
            preview,
            fallback: false
          });
        }
      }).catch(() => {
        if (active) {
          setState({
            type: "ready",
            preview: fallbackPreview(href, tweetId),
            fallback: true
          });
        }
      });
      return () => {
        active = false;
      };
    };
    t3 = [href, tweetId];
    $[1] = href;
    $[2] = tweetId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== href) {
    t4 = event => {
      stopCardEvent(event);
      navigator.clipboard.writeText(href).then(() => {
        setCopied(true);
        globalThis.window.setTimeout(() => setCopied(false), 900);
      });
    };
    $[5] = href;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const copyLink = t4;
  let t5;
  if ($[7] !== href || $[8] !== state.preview || $[9] !== state.type || $[10] !== tweetId) {
    t5 = state.type === "ready" ? state.preview : fallbackPreview(href, tweetId);
    $[7] = href;
    $[8] = state.preview;
    $[9] = state.type;
    $[10] = tweetId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const preview_0 = t5;
  const authorName = preview_0.authorName ?? preview_0.authorHandle ?? (state.type === "ready" ? "Twitter post" : " ");
  const authorHref = preview_0.authorUrl ?? (preview_0.authorHandle ? `https://x.com/${preview_0.authorHandle}` : href);
  const followHref = preview_0.authorHandle ? `https://x.com/intent/follow?screen_name=${encodeURIComponent(preview_0.authorHandle)}` : undefined;
  const t6 = preview_0.url || href;
  let t7;
  if ($[12] !== t6) {
    t7 = getXPostPath(t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const xPostPath = t7;
  let t8;
  if ($[14] !== preview_0) {
    t8 = formatTweetTimestamp(preview_0);
    $[14] = preview_0;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const timestamp = t8;
  if (state.type === "loading") {
    let t9;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <LoadingCard />;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  }
  if (state.fallback) {
    let t9;
    if ($[17] !== copied || $[18] !== copyLink || $[19] !== href) {
      t9 = <UnavailableCard href={href} copied={copied} onCopy={copyLink} />;
      $[17] = copied;
      $[18] = copyLink;
      $[19] = href;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    return t9;
  }
  let t9;
  if ($[21] !== href) {
    t9 = <Link href={href} target="_blank" rel="noopener noreferrer nofollow" aria-label="Open tweet on X" className="tw-absolute tw-inset-0 tw-z-0 tw-rounded-2xl" />;
    $[21] = href;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== authorHref || $[24] !== authorName || $[25] !== followHref || $[26] !== href || $[27] !== preview_0) {
    t10 = <TweetHeader authorHref={authorHref} authorName={authorName} followHref={followHref} href={href} preview={preview_0} />;
    $[23] = authorHref;
    $[24] = authorName;
    $[25] = followHref;
    $[26] = href;
    $[27] = preview_0;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== authorName || $[30] !== href || $[31] !== preview_0.replyToHandle || $[32] !== preview_0.text) {
    t11 = <TweetBodyText authorName={authorName} href={href} replyToHandle={preview_0.replyToHandle} text={preview_0.text} />;
    $[29] = authorName;
    $[30] = href;
    $[31] = preview_0.replyToHandle;
    $[32] = preview_0.text;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== authorName || $[35] !== href || $[36] !== preview_0) {
    t12 = <TweetMedia authorName={authorName} href={href} preview={preview_0} />;
    $[34] = authorName;
    $[35] = href;
    $[36] = preview_0;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const t13 = timestamp ?? `x.com${xPostPath}`;
  let t14;
  if ($[38] !== href || $[39] !== t13) {
    t14 = <Link href={href} target="_blank" rel="noopener noreferrer nofollow" onClick={stopCardEvent} className="tw-min-w-0 tw-truncate tw-text-[#8b98a5] tw-no-underline hover:tw-text-[#8b98a5] focus:tw-text-[#8b98a5]">{t13}</Link>;
    $[38] = href;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== preview_0.viewCount) {
    t15 = preview_0.viewCount !== undefined && preview_0.viewCount > 0 && <><span>·</span><span className="tw-flex-shrink-0">{formatViews(preview_0.viewCount)}</span></>;
    $[41] = preview_0.viewCount;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t14 || $[44] !== t15) {
    t16 = <div className="tw-flex tw-items-center tw-gap-x-2 tw-border-0 tw-border-b tw-border-solid tw-border-[#42566b] tw-pb-3 tw-text-sm tw-text-[#8b98a5]">{t14}{t15}</div>;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== hideActions || $[47] !== preview_0 || $[48] !== tweetId) {
    t17 = !hideActions && <TweetActions preview={preview_0} tweetId={tweetId} />;
    $[46] = hideActions;
    $[47] = preview_0;
    $[48] = tweetId;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t10 || $[51] !== t11 || $[52] !== t12 || $[53] !== t16 || $[54] !== t17) {
    t18 = <div className="tw-pointer-events-none tw-relative tw-z-10 tw-flex tw-flex-col tw-gap-y-3 tw-p-3 [&_a]:tw-pointer-events-auto [&_button]:tw-pointer-events-auto [&_video]:tw-pointer-events-auto">{t10}{t11}{t12}{t16}{t17}</div>;
    $[50] = t10;
    $[51] = t11;
    $[52] = t12;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== t18 || $[57] !== t9) {
    t19 = <article className={`${TWITTER_CARD_CLASSES} tw-relative`} data-testid="twitter-post-preview">{t9}{t18}</article>;
    $[56] = t18;
    $[57] = t9;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
}